import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { canManage, canEditProject } from '../permissions';
import { TableSkeleton } from '../components/Skeleton';
import AwardModal from '../components/AwardModal';
import { formatBidNumber } from '../utils/bidNumber';
import { parseWallClock } from '../utils/datetime';

// Award-required fields (mirrors AwardModal / the server gate). A project is
// missing one when its date is empty or its bid amount is unset/zero.
function awardMissing(p) {
  const out = [];
  if (!p.EstStartDate) out.push('EstStartDate');
  if (!p.ProjectCost || Number(p.ProjectCost) === 0) out.push('ProjectCost');
  if (!p.DateAwarded) out.push('DateAwarded');
  return out;
}

const STATUS_CLASS = {
  'Bidding': 'status-bidding',
  'Pending': 'status-pending',
  'Budget': 'status-budget',
  'On Hold': 'status-on-hold',
  'Pipeline': 'status-pipeline',
  'Awarded': 'status-awarded',
  'Not Awarded': 'status-not-awarded',
};

const STATUS_DISPLAY = { 'Pipeline': 'Opportunities' };
function displayStatus(s) { return STATUS_DISPLAY[s] || s; }

function formatCurrency(val) {
  if (!val || val === 0) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

function formatDate(val) {
  if (!val) return '-';
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function formatDateTime(val) {
  const d = parseWallClock(val);
  if (!d) return '-';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function highlightMatch(text, query) {
  if (!query || !text) return text || '-';
  const str = String(text);
  const idx = str.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return str;
  return (
    <>
      {str.slice(0, idx)}
      <mark style={{ background: '#fef3c7', padding: '1px 2px', borderRadius: '2px' }}>{str.slice(idx, idx + query.length)}</mark>
      {str.slice(idx + query.length)}
    </>
  );
}

const ALL_COLUMNS = [
  // First eight columns are pinned to this order by stakeholder request.
  // Users can still re-order via Layout prefs; this just sets the default.
  { id: 'BidNumber',              label: 'Bid #',              defaultOn: true,  sortType: 'number',   align: 'left' },
  { id: 'ProjectName',           label: 'Project Name',       defaultOn: true,  sortType: 'string',   align: 'left' },
  { id: 'EstBidStart',           label: 'Est. Bid Comm.',     defaultOn: true,  sortType: 'date',     align: 'left' },
  { id: 'InternalDeadline',      label: 'Bid Due to Mgr',     defaultOn: true,  sortType: 'date',     align: 'left' },
  { id: 'GCOwnerDeadline',       label: 'Owner Deadline',     defaultOn: true,  sortType: 'date',     align: 'left' },
  { id: 'Estimator',             label: 'Estimator',          defaultOn: true,  sortType: 'string',   align: 'left' },
  { id: 'SubDueDate',            label: 'Sub Bids Due',       defaultOn: true,  sortType: 'date',     align: 'left' },
  { id: 'BidCoordinator',        label: 'Est. Coord.',        defaultOn: true,  sortType: 'string',   align: 'left' },
  // Remaining columns (default-on by historical convention; users may hide).
  { id: 'ClientName',            label: 'Client',             defaultOn: true,  sortType: 'string',   align: 'left' },
  { id: 'StatusName',            label: 'Status',             defaultOn: true,  sortType: 'string',   align: 'left' },
  { id: 'ProjectType',           label: 'Type',               defaultOn: true,  sortType: 'string',   align: 'left' },
  { id: 'State',                 label: 'State',              defaultOn: true,  sortType: 'string',   align: 'left' },
  { id: 'WinProbability',        label: 'Win Prob.',          defaultOn: true,  sortType: 'string',   align: 'left',  execOnly: true },
  { id: 'ITBReceived',           label: 'ITB Rec.',           defaultOn: true,  sortType: 'date',     align: 'left' },
  { id: 'Distributed',           label: 'Dist.',              defaultOn: true,  sortType: 'number',   align: 'center' },
  { id: 'EstStartDate',          label: 'Est. Start',         defaultOn: true,  sortType: 'date',     align: 'left' },
  { id: 'EstActualContractValue',label: 'Proj. Bid Amt',      defaultOn: true,  sortType: 'number',   align: 'right' },
  { id: 'ProjectCost',           label: 'Bid Amt',            defaultOn: true,  sortType: 'number',   align: 'right' },
  { id: 'ProjectExecutive',      label: 'Project Exec.',      defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'ProjectManager',        label: 'Project Mgr.',       defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'FinalReviewer',         label: 'Final Reviewer',     defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'BidType',               label: 'Bid Type',           defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'Address',               label: 'Address',            defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'City',                  label: 'City',               defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'Zip',                   label: 'Zip',                defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'BuildingSqFt',          label: 'Building SqFt',      defaultOn: false, sortType: 'number',   align: 'right' },
  { id: 'SiteSqFt',              label: 'Site SqFt',          defaultOn: false, sortType: 'number',   align: 'right' },
  { id: 'BidWalkDate',           label: 'Bid Walk',           defaultOn: false, sortType: 'date',     align: 'left' },
  { id: 'BufferHours',           label: 'Buffer Hrs',         defaultOn: false, sortType: 'number',   align: 'right' },
  { id: 'DateBidSubmitted',      label: 'Date Submitted',     defaultOn: false, sortType: 'date',     align: 'left' },
  { id: 'DateAwarded',           label: 'Date Awarded',       defaultOn: false, sortType: 'date',     align: 'left' },
  { id: 'EstCompletionDate',     label: 'Est. Completion',    defaultOn: false, sortType: 'date',     align: 'left' },
  { id: 'AwardedAmount',         label: 'Awarded Amt',        defaultOn: false, sortType: 'number',   align: 'right' },
  { id: 'AwardNumber',           label: 'Award #',            defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'AwardedGC',             label: 'Awarded GC',         defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'DistributedBy',         label: 'Distributed By',     defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'OwnerType',             label: 'Owner Type',         defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'Competitors',           label: 'Competitors',        defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'Notes',                 label: 'Notes',              defaultOn: false, sortType: 'string',   align: 'left' },
  { id: 'IsPriority',            label: 'Must Win',           defaultOn: false, sortType: 'number',   align: 'center' },
  { id: 'CreatedAt',             label: 'Created',            defaultOn: false, sortType: 'date',     align: 'left' },
  { id: 'UpdatedAt',             label: 'Last Updated',       defaultOn: false, sortType: 'date',     align: 'left' },
];

const DEFAULT_ORDER = ALL_COLUMNS.map(c => c.id);
const DEFAULT_HIDDEN = ALL_COLUMNS.filter(c => !c.defaultOn).map(c => c.id);

// First-eight columns are pinned: cannot be hidden, cannot be reordered, and
// always render in this exact order regardless of saved LayoutPrefs.
const LOCKED_COLUMN_IDS = [
  'BidNumber',
  'ProjectName',
  'EstBidStart',
  'InternalDeadline',
  'GCOwnerDeadline',
  'Estimator',
  'SubDueDate',
  'BidCoordinator',
];
const LOCKED_SET = new Set(LOCKED_COLUMN_IDS);

// Force locked IDs to canonical positions 0..N at the front of any order
// array, preserving the relative order of unlocked columns. Use this anywhere
// colOrder is initialized or mutated so the invariant always holds, including
// for users with older saved LayoutPrefs from before the lock existed.
function normalizeColOrder(order) {
  const seen = new Set();
  const unlocked = [];
  for (const id of order) {
    if (seen.has(id)) continue;
    seen.add(id);
    if (!LOCKED_SET.has(id)) unlocked.push(id);
  }
  // Append any IDs that exist in ALL_COLUMNS but were missing from the saved
  // order (e.g. new columns added since the user last saved).
  for (const c of ALL_COLUMNS) {
    if (!seen.has(c.id) && !LOCKED_SET.has(c.id)) unlocked.push(c.id);
  }
  return [...LOCKED_COLUMN_IDS, ...unlocked];
}

function renderCell(col, p, { search, editingStatus, setEditingStatus, statuses, loadData, canSeeWin, setAwardModal }) {
  switch (col.id) {
    case 'BidNumber':
      return <span className="bid-number">{search ? highlightMatch(formatBidNumber(p.BidNumber, p.RevisionNumber), search) : formatBidNumber(p.BidNumber, p.RevisionNumber)}</span>;
    case 'ProjectName':
      return <><span className="project-name">{search ? highlightMatch(p.ProjectName, search) : p.ProjectName}</span>{p.IsPriority && <span className="cm-must-win-tag">Must Win</span>}</>;
    case 'ClientName':
      return p.ClientName ? (
        <Link to={`/clients/${encodeURIComponent(p.ClientName)}`} onClick={e => e.stopPropagation()}
          style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
          onMouseEnter={e => e.target.style.textDecoration = 'underline'}
          onMouseLeave={e => e.target.style.textDecoration = 'none'}>
          {search ? highlightMatch(p.ClientName, search) : p.ClientName}
        </Link>
      ) : '-';
    case 'StatusName':
      return editingStatus === p.ProjectId ? (
        <select autoFocus value="" onChange={e => {
            const statusId = e.target.value;
            setEditingStatus(null);
            const chosen = statuses.find(s => String(s.StatusId) === String(statusId));
            // Flipping to Awarded with missing required fields → prompt for them.
            if (chosen && chosen.StatusName === 'Awarded' && awardMissing(p).length) {
              setAwardModal({ project: p, awardedStatusId: statusId });
              return;
            }
            api.updateStatus(p.ProjectId, statusId).then(loadData).catch(err => alert(err.message));
          }} onBlur={() => setEditingStatus(null)} style={{fontSize:'12px', padding:'3px 6px'}}>
          <option value="" disabled>Select...</option>
          {statuses.map(s => <option key={s.StatusId} value={s.StatusId}>{displayStatus(s.StatusName)}</option>)}
        </select>
      ) : (
        <span className={`status-badge ${STATUS_CLASS[p.StatusName] || ''}`}>{displayStatus(p.StatusName)}</span>
      );
    case 'WinProbability':
      if (!canSeeWin) return null;
      return p.WinProbability
        ? <span className={`win-prob-badge win-prob-${p.WinProbability.toLowerCase()}`}>{p.WinProbability}</span>
        : <span style={{ color: 'var(--color-gray-300)' }}>—</span>;
    case 'Distributed':
      return p.Distributed
        ? <span title={p.DistributedAt ? `Distributed ${new Date(p.DistributedAt).toLocaleString()}${p.DistributedBy ? ` by ${p.DistributedBy}` : ''}` : 'Distributed'} style={{ color: '#16a34a', fontWeight: 600 }}>✓</span>
        : <span style={{ color: 'var(--color-gray-300)' }}>—</span>;
    case 'IsPriority':
      return p.IsPriority ? <span style={{ color: '#b8860b', fontWeight: 600 }}>★</span> : <span style={{ color: 'var(--color-gray-300)' }}>—</span>;
    case 'ProjectCost':
    case 'EstActualContractValue':
    case 'AwardedAmount':
      return <span className="currency">{formatCurrency(p[col.id])}</span>;
    case 'BuildingSqFt':
    case 'SiteSqFt':
      return p[col.id] ? p[col.id].toLocaleString() : '-';
    case 'BufferHours':
      return p[col.id] != null ? p[col.id] : '-';
    case 'SubDueDate':
    case 'GCOwnerDeadline':
    case 'InternalDeadline':
    case 'DateBidSubmitted':
      return formatDateTime(p[col.id]);
    case 'ITBReceived':
    case 'EstBidStart':
    case 'EstStartDate':
    case 'EstCompletionDate':
    case 'DateAwarded':
    case 'BidWalkDate':
    case 'CreatedAt':
    case 'UpdatedAt':
      return formatDate(p[col.id]);
    case 'Notes':
    case 'Competitors':
      const txt = p[col.id] || '';
      return txt.length > 40 ? <span title={txt}>{txt.substring(0, 40)}...</span> : (txt || '-');
    default:
      return p[col.id] || '-';
  }
}

export default function ProjectsPage({ myBids }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [estimatorFilter, setEstimatorFilter] = useState('');
  const [coordinatorFilter, setCoordinatorFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [myStatusFilter, setMyStatusFilter] = useState('');
  const [estimators, setEstimators] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const { user } = useAuth();
  const canSeeWin = canManage(user);
  const canMgr = canManage(user);

  const [sortCol, setSortCol] = useState(() => user?.layoutPrefs?.sortCol || 'BidNumber');
  const [sortDir, setSortDir] = useState(() => user?.layoutPrefs?.sortDir || 'desc');
  const [editingStatus, setEditingStatus] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [awardModal, setAwardModal] = useState(null); // { project, awardedStatusId }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');

  const [layoutMode, setLayoutMode] = useState(false);
  const [colOrder, setColOrder] = useState(() => normalizeColOrder(user?.layoutPrefs?.order || DEFAULT_ORDER));
  const [colHidden, setColHidden] = useState(() => {
    const saved = new Set(user?.layoutPrefs?.hidden || DEFAULT_HIDDEN);
    // Locked columns cannot be hidden — strip them from the hidden set on load.
    for (const id of LOCKED_COLUMN_IDS) saved.delete(id);
    return saved;
  });
  useEffect(() => {
    if (user?.layoutPrefs) {
      if (user.layoutPrefs.order) setColOrder(normalizeColOrder(user.layoutPrefs.order));
      if (user.layoutPrefs.hidden) {
        const h = new Set(user.layoutPrefs.hidden);
        for (const id of LOCKED_COLUMN_IDS) h.delete(id);
        setColHidden(h);
      }
    }
  }, [user?.layoutPrefs]);

  const visibleColumns = useMemo(() => {
    const colMap = new Map(ALL_COLUMNS.map(c => [c.id, c]));
    return colOrder
      .filter(id => !colHidden.has(id) && colMap.has(id))
      .filter(id => {
        const col = colMap.get(id);
        if (col.execOnly && !canSeeWin) return false;
        return true;
      })
      .map(id => colMap.get(id));
  }, [colOrder, colHidden, canSeeWin]);

  const availableColumns = useMemo(() => {
    const colMap = new Map(ALL_COLUMNS.map(c => [c.id, c]));
    return colOrder
      .filter(id => colMap.has(id))
      .filter(id => {
        const col = colMap.get(id);
        if (col.execOnly && !canSeeWin) return false;
        return true;
      })
      .map(id => colMap.get(id));
  }, [colOrder, canSeeWin]);

  const statusFilter = searchParams.get('status') || '';
  const archivedFilter = searchParams.get('archived') || '';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (archivedFilter) params.archived = archivedFilter;
      if (search) params.search = search;
      if (estimatorFilter) params.estimator = estimatorFilter;
      if (coordinatorFilter) params.coordinator = coordinatorFilter;
      if (stateFilter) params.state = stateFilter;
      if (myBids && user?.displayName) {
        if (user.role === 'bid_coordinator') params.coordinator = user.displayName;
        else params.estimator = user.displayName;
        if (myStatusFilter) params.status = myStatusFilter;
      }
      const data = await api.getProjects(params);
      setProjects(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [statusFilter, archivedFilter, search, estimatorFilter, coordinatorFilter, stateFilter, myBids, myStatusFilter, user]);

  useEffect(() => {
    loadData();
    api.getEstimators().then(setEstimators).catch(() => {});
    api.getCoordinators().then(setCoordinators).catch(() => {});
    api.getStatuses().then(setStatuses).catch(() => {});
  }, [loadData]);

  const handleSort = (col) => {
    let newCol = col;
    let newDir;
    if (sortCol === col) {
      newDir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(newDir);
    } else {
      newDir = 'asc';
      setSortCol(col);
      setSortDir(newDir);
    }
    if (localStorage.getItem('bidboard-remember-sort') === 'true') {
      fetch('/api/auth/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ layoutPrefs: { ...user?.layoutPrefs, sortCol: newCol, sortDir: newDir } }),
      }).catch(() => {});
    }
  };

  const sortedProjects = useMemo(() => {
    const colDef = ALL_COLUMNS.find(c => c.id === sortCol);
    const st = colDef?.sortType || 'string';
    return [...projects].sort((a, b) => {
      let aVal = a[sortCol];
      let bVal = b[sortCol];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      let cmp = 0;
      if (st === 'date') cmp = new Date(aVal) - new Date(bVal);
      else if (st === 'number') cmp = Number(aVal) - Number(bVal);
      else cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [projects, sortCol, sortDir]);

  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  function handlePanelDragStart(idx) {
    if (LOCKED_SET.has(colOrder[idx])) return; // locked columns cannot be dragged
    setDragIdx(idx);
  }
  function handlePanelDragOver(e, idx) { e.preventDefault(); setDragOverIdx(idx); }
  function handlePanelDrop(idx) {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    // Don't allow dropping into a slot occupied by a locked column, or moving
    // a locked column. Both invariants are belt-and-suspenders since the UI
    // already disables drag for locked rows.
    if (LOCKED_SET.has(colOrder[dragIdx]) || LOCKED_SET.has(colOrder[idx])) {
      setDragIdx(null); setDragOverIdx(null); return;
    }
    setColOrder(prev => {
      const arr = [...prev];
      const item = arr.splice(dragIdx, 1)[0];
      arr.splice(idx, 0, item);
      return normalizeColOrder(arr);
    });
    setDragIdx(null);
    setDragOverIdx(null);
  }
  function handlePanelDragEnd() { setDragIdx(null); setDragOverIdx(null); }

  function moveColumn(fromIdx, dir) {
    const toIdx = fromIdx + dir;
    if (toIdx < 0 || toIdx >= colOrder.length) return;
    if (LOCKED_SET.has(colOrder[fromIdx]) || LOCKED_SET.has(colOrder[toIdx])) return;
    setColOrder(prev => {
      const arr = [...prev];
      [arr[fromIdx], arr[toIdx]] = [arr[toIdx], arr[fromIdx]];
      return normalizeColOrder(arr);
    });
  }

  function toggleColumn(colId) {
    if (LOCKED_SET.has(colId)) return; // locked columns cannot be hidden
    setColHidden(prev => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId); else next.add(colId);
      return next;
    });
  }

  async function saveLayout() {
    const prefs = { order: colOrder, hidden: [...colHidden] };
    try {
      await fetch('/api/auth/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ layoutPrefs: prefs }),
      });
    } catch (e) { console.error('Failed to save layout:', e); }
    setLayoutMode(false);
  }

  function resetLayout() {
    setColOrder(normalizeColOrder(DEFAULT_ORDER));
    const hidden = new Set(DEFAULT_HIDDEN);
    for (const id of LOCKED_COLUMN_IDS) hidden.delete(id);
    setColHidden(hidden);
  }

  const pageTitle = myBids ? `My Bids — ${user?.displayName || ''}` : archivedFilter ? 'Archived Projects' : (statusFilter || 'All Projects');
  const totalValue = projects.reduce((sum, p) => sum + (p.ProjectCost || p.EstActualContractValue || 0), 0);
  const projectedTotalValue = projects.reduce((sum, p) => sum + (p.EstActualContractValue || p.ProjectCost || 0), 0);
  const awardedCount = projects.filter(p => p.StatusName === 'Awarded').length;
  const activeBids = projects.filter(p => p.StatusName === 'Bidding' || p.StatusName === 'Pending').length;
  const decided = awardedCount + projects.filter(p => p.StatusName === 'Not Awarded').length;
  const winRate = decided > 0 ? Math.round((awardedCount / decided) * 100) : 0;

  const cellCtx = { search, editingStatus, setEditingStatus, statuses, loadData, canSeeWin, setAwardModal };

  return (
    <>
      <div className="top-bar">
        <h2>{pageTitle}</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {!layoutMode ? (
            <button className="btn btn-secondary btn-sm" onClick={() => setLayoutMode(true)} title="Customize column order and visibility">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{verticalAlign:'middle',marginRight:'4px'}}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              Edit Layout
            </button>
          ) : (
            <>
              <button className="btn btn-primary btn-sm" onClick={saveLayout}>Done</button>
              <button className="btn btn-secondary btn-sm" onClick={resetLayout}>Reset to Default</button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setLayoutMode(false); if (user?.layoutPrefs) { setColOrder(user.layoutPrefs.order || DEFAULT_ORDER); setColHidden(new Set(user.layoutPrefs.hidden || DEFAULT_HIDDEN)); } }}>Cancel</button>
            </>
          )}
          <button className="btn btn-secondary" onClick={() => window.open('/api/projects/export')}>Export</button>
          <Link to="/projects/new" className="btn btn-primary">+ New Project</Link>
        </div>
      </div>

      <div className="page-content fixed-layout">
        {layoutMode && (
          <div className="layout-panel">
            <div className="layout-panel-header">
              <div className="layout-panel-title">Column Layout</div>
              <div className="layout-panel-hint">Drag rows to reorder. Uncheck to hide.</div>
            </div>
            <div className="layout-panel-list">
              {availableColumns.map((col, idx) => {
                const globalIdx = colOrder.indexOf(col.id);
                const isHidden = colHidden.has(col.id);
                const isLocked = LOCKED_SET.has(col.id);
                return (
                  <div
                    key={col.id}
                    className={`layout-panel-row ${dragOverIdx === globalIdx ? 'layout-drop-target' : ''} ${dragIdx === globalIdx ? 'layout-dragging' : ''} ${isHidden ? 'layout-row-hidden' : ''} ${isLocked ? 'layout-row-locked' : ''}`}
                    draggable={!isLocked}
                    onDragStart={() => handlePanelDragStart(globalIdx)}
                    onDragOver={e => handlePanelDragOver(e, globalIdx)}
                    onDrop={() => handlePanelDrop(globalIdx)}
                    onDragEnd={handlePanelDragEnd}
                    title={isLocked ? 'Pinned column — cannot be moved or hidden' : undefined}
                  >
                    <span className="layout-row-grip">{isLocked ? '🔒' : '⠿'}</span>
                    <input type="checkbox" checked={!isHidden} onChange={() => toggleColumn(col.id)} disabled={isLocked} />
                    <span className="layout-row-label">{col.label}</span>
                    <span className="layout-row-arrows">
                      <button className="layout-arrow" onClick={() => moveColumn(globalIdx, -1)} disabled={isLocked || globalIdx === 0 || LOCKED_SET.has(colOrder[globalIdx - 1])} title="Move up">&uarr;</button>
                      <button className="layout-arrow" onClick={() => moveColumn(globalIdx, 1)} disabled={isLocked || globalIdx === colOrder.length - 1} title="Move down">&darr;</button>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="stats-row">
          <div className="stat-card"><div className="stat-label">Projects</div><div className="stat-value">{projects.length}</div></div>
          <div className="stat-card"><div className="stat-label">Total Value</div><div className="stat-value">{formatCurrency(totalValue)}</div></div>
          <div className="stat-card"><div className="stat-label">Projected Total Value</div><div className="stat-value">{formatCurrency(projectedTotalValue)}</div></div>
          <div className="stat-card"><div className="stat-label">Awarded</div><div className="stat-value">{awardedCount}</div></div>
          <div className="stat-card"><div className="stat-label">Win Rate</div><div className="stat-value">{winRate}%</div></div>
          <div className="stat-card"><div className="stat-label">Active Bids</div><div className="stat-value">{activeBids}</div></div>
        </div>

        <div className="filters-bar">
          <div className="filter-group">
            <label>Search</label>
            <input id="global-search" type="text" className="filter-input search-input" placeholder="Search projects, bids, clients..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadData()} />
          </div>
          <div className="filter-group">
            <label>Estimator</label>
            <select className="filter-select" value={estimatorFilter} onChange={e => setEstimatorFilter(e.target.value)}>
              <option value="">All</option>
              {estimators.map(t => <option key={t.TeamMemberId} value={t.FullName}>{t.FullName}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Estimating Coordinator</label>
            <select className="filter-select" value={coordinatorFilter} onChange={e => setCoordinatorFilter(e.target.value)}>
              <option value="">All</option>
              {coordinators.map(t => <option key={t.TeamMemberId} value={t.FullName}>{t.FullName}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>State</label>
            <select className="filter-select" value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
              <option value="">All</option>
              <option value="CA">CA</option><option value="AZ">AZ</option><option value="NV">NV</option>
              <option value="UT">UT</option><option value="OR">OR</option><option value="WA">WA</option>
            </select>
          </div>
          {myBids && (
            <div className="filter-group">
              <label>Status</label>
              <select className="filter-select" value={myStatusFilter} onChange={e => setMyStatusFilter(e.target.value)}>
                <option value="">All</option>
                {statuses.map(s => <option key={s.StatusId} value={s.StatusName}>{displayStatus(s.StatusName)}</option>)}
              </select>
            </div>
          )}
          <div className="filter-group">
            <label>&nbsp;</label>
            <button className="btn btn-secondary btn-sm" onClick={loadData}>Apply</button>
          </div>
        </div>

        {canMgr && selectedIds.size > 0 && (
          <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px', padding:'12px 16px', background:'#eef2ff', borderRadius:'8px', border:'1px solid #c7d2fe', flexWrap:'wrap'}}>
            <span style={{fontWeight:600, fontSize:'13px'}}>{selectedIds.size} selected</span>
            <select value={bulkField} onChange={e => { setBulkField(e.target.value); setBulkValue(''); }} style={{padding:'6px 10px', borderRadius:'6px', border:'1px solid #c7d2fe', fontSize:'13px'}}>
              <option value="">Choose field...</option>
              <option value="StatusId">Status</option>
              {canSeeWin && <option value="WinProbability">Win Probability</option>}
              <option value="Estimator">Estimator</option>
              <option value="BidCoordinator">Estimating Coordinator</option>
              <option value="State">State</option>
            </select>
            {bulkField === 'StatusId' && (
              <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{padding:'6px 10px', borderRadius:'6px', border:'1px solid #c7d2fe', fontSize:'13px'}}>
                <option value="">Select...</option>
                {statuses.map(s => <option key={s.StatusId} value={s.StatusId}>{displayStatus(s.StatusName)}</option>)}
              </select>
            )}
            {bulkField === 'WinProbability' && (
              <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{padding:'6px 10px', borderRadius:'6px', border:'1px solid #c7d2fe', fontSize:'13px'}}>
                <option value="">Select...</option>
                <option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option>
              </select>
            )}
            {bulkField === 'Estimator' && (
              <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{padding:'6px 10px', borderRadius:'6px', border:'1px solid #c7d2fe', fontSize:'13px'}}>
                <option value="">Select...</option>
                {estimators.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            )}
            {bulkField === 'BidCoordinator' && (
              <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{padding:'6px 10px', borderRadius:'6px', border:'1px solid #c7d2fe', fontSize:'13px'}}>
                <option value="">Select...</option>
                {coordinators.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {bulkField === 'State' && (
              <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{padding:'6px 10px', borderRadius:'6px', border:'1px solid #c7d2fe', fontSize:'13px'}}>
                <option value="">Select...</option>
                {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            <button className="btn btn-accent btn-sm" disabled={!bulkField || !bulkValue} onClick={() => {
              if (!window.confirm(`Update ${selectedIds.size} project(s)?`)) return;
              fetch('/api/projects/bulk-update', {method:'PATCH', headers:{'Content-Type':'application/json'}, credentials:'same-origin', body:JSON.stringify({projectIds: [...selectedIds], field: bulkField, value: bulkValue})})
                .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.error); }); return r.json(); })
                .then(res => {
                  loadData(); setSelectedIds(new Set()); setBulkField(''); setBulkValue('');
                  if (res && res.rejected && res.rejected.length) {
                    alert(`${res.rejected.length} project(s) were not marked Awarded — they're missing required fields (Est. Start Date, Bid Amount, Date Awarded). Set those on each project first.`);
                  }
                })
                .catch(err => alert('Bulk update failed: ' + err.message));
            }}>Apply to {selectedIds.size}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedIds(new Set()); setBulkField(''); setBulkValue(''); }}>Clear</button>
            {canManage(user) && (
              <button className="btn btn-secondary btn-sm" onClick={() => {
                const action = archivedFilter ? 'Unarchive' : 'Archive';
                if (!window.confirm(`${action} ${selectedIds.size} project(s)?`)) return;
                api.bulkArchive([...selectedIds], !archivedFilter)
                  .then(() => { loadData(); setSelectedIds(new Set()); })
                  .catch(err => alert(`${action} failed: ` + err.message));
              }}>{archivedFilter ? 'Unarchive' : 'Archive'} {selectedIds.size}</button>
            )}
          </div>
        )}

        <div className="card">
          <div className="table-wrapper">
            <table className={`data-table${canMgr ? '' : ' no-select-col'}`}>
              <thead>
                <tr>
                  {canMgr && (
                    <th className="col-sticky-checkbox" style={{width:'40px'}}>
                      <input type="checkbox" onChange={e => {
                        if (e.target.checked) setSelectedIds(new Set(sortedProjects.map(p => p.ProjectId)));
                        else setSelectedIds(new Set());
                      }} />
                    </th>
                  )}
                  {visibleColumns.map(col => (
                    <th
                      key={col.id}
                      onClick={() => handleSort(col.id)}
                      className={col.id === 'BidNumber' ? 'col-sticky-bidnumber' : undefined}
                      style={{ cursor: 'pointer', textAlign: col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : undefined }}
                    >
                      {col.label} {sortCol === col.id ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton />
                ) : sortedProjects.length === 0 ? (
                  <tr><td colSpan={visibleColumns.length + (canMgr ? 1 : 0)}>
                    <div className="empty-state">
                      <h3>No projects found</h3>
                      <p>Try adjusting your filters or add a new project.</p>
                    </div>
                  </td></tr>
                ) : sortedProjects.map(p => (
                  <tr key={p.ProjectId} className={`clickable-row ${p.IsPriority ? 'cm-priority-row' : ''}`} onClick={() => navigate(`/projects/${p.ProjectId}`)}>
                    {canMgr && (
                      <td className="col-sticky-checkbox" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(p.ProjectId)} onChange={() => {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (next.has(p.ProjectId)) next.delete(p.ProjectId); else next.add(p.ProjectId);
                            return next;
                          });
                        }} />
                      </td>
                    )}
                    {visibleColumns.map(col => (
                      <td key={col.id}
                        onClick={col.id === 'StatusName' ? (e => { e.stopPropagation(); if (canEditProject(user, p)) setEditingStatus(p.ProjectId); }) : undefined}
                        className={col.id === 'BidNumber' ? 'col-sticky-bidnumber' : undefined}
                        style={{ textAlign: col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : undefined }}
                      >
                        {renderCell(col, p, cellCtx)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {awardModal && (
        <AwardModal
          project={awardModal.project}
          awardedStatusId={awardModal.awardedStatusId}
          onClose={() => setAwardModal(null)}
          onAwarded={() => { setAwardModal(null); loadData(); }}
        />
      )}
    </>
  );
}
